import React from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { FieldComponent, withModalContext } from '@jutro/components';
import { withValidation, validationPropTypes } from '@xengage/gw-portals-validation-react';
import { ServiceManager } from '@jutro/legacy/services';
import { loadGoogleMapsAPI } from '@xengage/gw-portals-google-maps-js';
import { ViewModelForm } from '@xengage/gw-portals-viewmodel-react';
import { messages as commonMessages } from '@xengage/gw-platform-translations';
import metadata from './GooglePlacesLookupComponent.metadata.json5';
import styles from './GooglePlacesLookupComponent.module.scss';
import messages from './GooglePlacesLookupComponent.messages';

class GooglePlacesLookupComponent extends FieldComponent {
    /**
     * @memberof gw-capability-address-react.GooglePlacesLookupComponent
     * @prop {Object} propTypes - the props that are passed to this component
     * @prop {string} propTypes.postalCode - the postal code entered in the landing page
     * @prop {function} propTypes.onValueChange - callback when change is made
     * @prop {string} propTypes.path - path to value in the view modal
     * @prop {function} propTypes.onValidate - returns if the values are valid
     */
    static propTypes = {
        value: PropTypes.shape({ postalCode: PropTypes.string }),
        path: PropTypes.string.isRequired,
        id: PropTypes.string.isRequired,
        onValidate: PropTypes.func,
        onValueChange: PropTypes.func,
        label: PropTypes.string,
        labelPosition: PropTypes.string,
        required: PropTypes.bool,
        showCountry:PropTypes.bool,
        isOseStates:PropTypes.bool,
        ...FieldComponent.propTypes,
        ...validationPropTypes
    };

    static defaultProps = {
        onValidate: undefined,
        value: undefined,
        onValueChange: undefined,
        layout: 'full-width',
        hideLabel: true,
        label: undefined,
        labelPosition: undefined,
        required: false,
        isOseStates: false
    };

    searchInputRef = null;

    selectedGoogleDropdown = false;

    state = {
        errorText: '',
        formData: {},
        manualAddressOverride: undefined
    };

    componentDidMount() {
        const {
            registerComponentValidation, isComponentValid, onValidate, id, selectedCountry
        } = this.props;
        const { formData } = this.state;
        const localeService = ServiceManager.getService('locale-service');

        registerComponentValidation(this.isFieldsValid);

        if (onValidate) {
            onValidate(isComponentValid, id);
        }

        const { value: addressData } = this.props;
        
        const country = selectedCountry !== undefined ? selectedCountry : localeService.getDefaultCountryCode()

        const options = {
            componentRestrictions: {
                country: country
            }
        };
        const _this = this;
        loadGoogleMapsAPI().then((response) => {
            const googleMapsApi = response;

            const autocomplete = new googleMapsApi.places.Autocomplete(
                _this.searchInputRef,
                options
            );
            googleMapsApi.event.addListener(autocomplete, 'place_changed', () => { 
                _this.selectedGoogleDropdown = true;
                const place = autocomplete.getPlace();
                const valueSelected = place.formatted_address;
                // const postalCodeComponent = _.find(place.address_components, (addressComponent) => {
                //     return _.includes(addressComponent.types, 'postal_code');
                // });
                _this.mapGooglePlacesAddressToDTO(place);

                // if (postalCodeComponent !== undefined) {
                //     if (addressData.postalCode.value === postalCodeComponent.long_name) {
                //         _this.mapGooglePlacesAddressToDTO(place);

                //         // manually set the value returned by the google API
                //         // into the search input field. The value returned is
                //         // a string composed by addressLine1, City, State, Cuntry
                //         // separated by comma
                //         const { formData } = _this.state;
                //         _.set(formData, 'searchText', valueSelected);
                //         _this.setState({ formData });
                //     } else {
                //         ModalNextProvider.showAlert({
                //             title: messages.availabilityLookupErrorTitle,
                //             message: messages.availabilityLookupErrorMessage,
                //             status: 'error',
                //             icon: 'mi-error-outline',
                //             confirmButtonText: commonMessages.ok
                //         }).catch(_.noop);
                //         _this.clearTextField();
                //     }
                // }
            });
        });
    }

    mapGooglePlacesAddressToDTO = (place) => {
        const { value: addressData } = this.props;
        const { modalContext , translator} = this.props;
        place.address_components.forEach((addressComponent) => {
            if (addressComponent.types.includes('street_number')) {
                addressData.addressLine1.value = addressComponent.long_name;
                this.handleChange(addressComponent.long_name, 'addressLine1');
            }
            if (addressComponent.types.includes('route')) {
                const streetNumber = addressData.addressLine1.value;
                addressData.addressLine1.value = `${streetNumber} ${addressComponent.long_name}`;
                this.handleChange(`${streetNumber} ${addressComponent.long_name}`, 'addressLine1');
            }
            if (
                addressComponent.types.includes('neighborhood')
                && addressComponent.types.includes('political')
            ) {
                addressData.addressLine2.value = addressComponent.long_name;
                this.handleChange(addressComponent.long_name, 'addressLine2');
            }
            if (
                addressComponent.types.includes('locality')
                && addressComponent.types.includes('political')
            ) {
                addressData.city.value = addressComponent.long_name;
                this.handleChange(addressComponent.long_name, 'city');
            }
            if (
                addressComponent.types.includes('sublocality_level_1')
                && addressComponent.types.includes('sublocality')
                && addressComponent.types.includes('political')
            ) {
                addressData.city.value = addressComponent.long_name;
                this.handleChange(addressComponent.long_name, 'city');
            }
            if (addressComponent.types.includes('postal_code')) {
                addressData.postalCode.value = addressComponent.long_name;
                this.handleChange(addressComponent.long_name, 'postalCode');                                
            }
            if (
                addressComponent.types.includes('administrative_area_level_1')
                && addressComponent.types.includes('political')
            ) {
                if (addressData.state.value || addressComponent.short_name) {
                    if(addressData.value.country === "MX"){
                        addressData.state.aspects.availableValues.forEach((val) =>{
                            const stateDisplay = translator({
                                id: val.name,
                                defaultMessage: val.name,
                            })
                            if(stateDisplay === addressComponent.long_name){
                                addressData.state.value = val.code;
                                this.handleChange(val.code, 'state');
                            }
                        })                        
                    } else{
                        addressData.state.value = addressComponent.short_name;
                        this.handleChange(addressComponent.short_name, 'state');
                    }                   
                }
            }
        });
        if (_.isEmpty(addressData.addressLine1.value) || _.isEmpty(addressData.city.value)) {
            modalContext.showAlert({
                title: messages.unsuccessfulMappingTitle,
                message: messages.unsuccessfulMappingMessage,
                status: 'error',
                icon: 'mi-error-outline',
                confirmButtonText: commonMessages.ok
            }).catch(_.noop);
        }
    };

    isFieldsValid = () => {
        const { value: address } = this.props;

        return address?.aspects?.valid && address?.aspects?.subtreeValid;
    };

    componentDidUpdate(prevProps) {
        const { value: oldAddressData } = prevProps;
        const {
            isComponentValid,
            value: newAddressData,
            hasValidationChanged,
            onValidate,
            id
        } = this.props;

        const isAddressTheSame = _.isEqual(newAddressData?.value, oldAddressData?.value);

        if (onValidate && (!isAddressTheSame || hasValidationChanged)) {
            onValidate(isComponentValid, id);
        }
    }

    handleChange = (value, changedPath) => {
        const { path, onValueChange, conutryChange, employeeLocationChange } = this.props;
        const { formData } = this.state;

        _.set(formData, changedPath, value);
        onValueChange(value, `${path}.${changedPath}`);

        this.setState({ formData });

        if(changedPath === "country") {    
            this.handleChange('', 'addressLine1')
            this.handleChange('', 'addressLine2')
            this.handleChange('', 'addressLine3')
            this.handleChange('', 'city')
            this.handleChange(undefined, 'state')
            this.handleChange('', 'postalCode')           
            _.set(formData, changedPath, value);
            this.setState({ formData });
            conutryChange(value)            
        }
  
        if(employeeLocationChange)    {
            employeeLocationChange()
        }  
    };

    shouldComponentUpdate = () => {
        return true;
    };

    writeValue = (value, path) => {
        const { formData } = this.state;
        _.set(formData, path, value);
        this.setState({
            formData
        });
    };

    writeValueFormAddress = (value, path) => {
        const { formData } = this.state;
        const { value:address, employeeLocationChange }=this.props;
        _.set(formData, 'state', value);
        this.setState({
            formData
        });
        
        _.set(address, 'state', value);
        this.setState({
            address
        });

        if(employeeLocationChange){
            employeeLocationChange()
        }
    };

    clearTextField = () => {
        const { formData } = this.state;
        _.set(formData, 'addressLine1', '');
        this.selectedGoogleDropdown = false;
        this.setState({ formData });
    };

    renderErrorsMessage = () => {
        const { errorText } = this.state;
        return [errorText];
    };

    setupSearchInputRef = (ref) => {
        if (!_.isNull(ref)) {
            this.searchInputRef = ref.querySelector('input[name=addressLine1]');
        }
    };

    renderControl() {
        const { formData, manualAddressOverride } = this.state;
        const {
            label, required, labelPosition, value: address, isComponentValid,id, showCountry, isOseStates, countryAvailableValues,availableStateValues,page
        } = this.props;
        
        const dataForComponent = {
            ...formData,
            ...address
        };
        const translator = this.context;
       
        const showManualAddress = !!manualAddressOverride
            || (isComponentValid
                && !this.selectedGoogleDropdown
                && manualAddressOverride === undefined);
        const showLookupAddress = !showManualAddress;
        const oseStatesAvailableValues = address?.oseStates?.aspects?.availableValues?.map(s => ({code: s.code, name: translator({id: s.name})}));
        const stateAvailableValuesConfig = {};

        if (oseStatesAvailableValues?.length > 0 && isOseStates) {
            stateAvailableValuesConfig['availableValues'] = oseStatesAvailableValues;
        }

        const overrideProps = {
            '@field': {
                showOptional: false,
                labelPosition: labelPosition,
                tablet: {
                    labelPosition: 'top'
                },
                phoneWide: {
                    labelPosition: 'top'
                }
            },
            country: {
                visible: showCountry !== undefined && showCountry,
                availableValues: countryAvailableValues
            },
            addressLine3:{
                visible:!!((id === "wherePageGoogleComponent" ||id=== "contactDetailsGoogleComponent"||id==="whatgoogleplaces")),
                

            },
            addressLine1:{
                labelClassName:id==="COIgoogleplaces"?"certificatePage":"addressLabel"
            },
            addresslookupSearch: {
                onValueChange: this.writeValue,
                label: label,
                required: required
            },
            addressLookupContainer: {
                // visible: showLookupAddress
                visible:true
            },
            state: {
                disabled: this.props.employeeLocationEditFlag === true?true:false,
                label: formData.country === "CA" ? "Province" : "State" ,
                placeholder: formData.country === "CA" ? "-- Choose Province --" : "-- Choose State --" ,
                visible:!!(page===undefined || page!=="Location"),
                ...stateAvailableValuesConfig
            },
            state1: {
                disabled: this.props.employeeLocationEditFlag === true?true:false,
                label: formData.country === "CA" ? "Province" : "State" ,
                placeholder: formData.country === "CA" ? "-- Choose Province --" : "-- Choose State --" ,
                availableValues:availableStateValues,
                onValueChange:this.writeValueFormAddress,
                value:formData.state,
                visible:page==="Location"
            },
            postalCode: {                       
                //visible: formData.country === "US",
                label: formData.country === "CA" ? "PostalCode" : "ZIP Code" ,
            },
        };
        const resolvers = {
            resolveClassNameMap: styles,
            resolveCallbackMap: {
            }
        };

        return (
            <div ref={this.setupSearchInputRef}>
                <ViewModelForm
                    uiProps={metadata.componentContent}
                    model={dataForComponent}
                    overrideProps={overrideProps}
                    onValueChange={this.handleChange}
                    classNameMap={resolvers.resolveClassNameMap}
                    callbackMap={resolvers.resolveCallbackMap}
                />
            </div>
        );
    }

    render() {
        return super.render();
    }
}

export default withValidation(GooglePlacesLookupComponent);